import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useToken } from '../auth/useToken';
import { deleteMe } from '../lib/me';
import { listConversations, type ConversationSummary } from '../lib/conversations';
import type { SavedPrompt } from '../lib/prompts';

/**
 * Phase-8 destructive confirmation dialog. Triggers `DELETE /me` on confirm,
 * then logs the user out (their next sign-in re-seeds via EnsureUserMiddleware).
 * Type-DELETE guard prevents accidental clicks; the Delete CTA stays disabled
 * until the exact word `DELETE` is in the input.
 *
 * Counts: takes `prompts` from the lifted state, and lazy-fetches the
 * conversations count on open if the parent didn't pass one.
 */
interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompts: SavedPrompt[];
  conversations?: ConversationSummary[];
}

export function DeleteAccountDialog({ open, onOpenChange, prompts, conversations }: DeleteAccountDialogProps) {
  const getToken = useToken();
  const { instance } = useMsal();
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [convCount, setConvCount] = useState<number | null>(conversations?.length ?? null);

  // Reset confirmation each time the dialog opens; refresh conv count on open
  // since prompts/conversations may have changed since last render.
  useEffect(() => {
    if (!open) return;
    setConfirmText('');
    setBusy(false);
    if (conversations === undefined) {
      (async () => {
        try {
          const list = await listConversations(await getToken());
          setConvCount(list.length);
        } catch { /* keep last known count */ }
      })();
    } else {
      setConvCount(conversations.length);
    }
  }, [open, conversations, getToken]);

  // Esc to close (when not busy).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onOpenChange]);

  if (!open) return null;

  const promptCount = prompts.length;
  const ready = confirmText.trim() === 'DELETE';

  const onConfirm = async () => {
    setBusy(true);
    try {
      await deleteMe(await getToken());
      instance.logoutRedirect();
    } catch (err) {
      console.error('deleteMe failed:', err);
      setBusy(false);
    }
  };

  return (
    <div
      className="delete-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Delete account"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onOpenChange(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="delete-dialog">
        <div className="delete-dialog__icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div className="delete-dialog__title">Delete your account?</div>
        <div className="delete-dialog__body">
          <p>
            This permanently removes your <strong>Kairos history</strong> — every conversation, every
            saved prompt — from our servers. This cannot be undone.
          </p>
          <ul>
            <li>
              <strong>
                {convCount ?? '…'} conversation{convCount === 1 ? '' : 's'}
              </strong>
              {' '}will be deleted
            </li>
            <li>
              <strong>
                {promptCount} saved prompt{promptCount === 1 ? '' : 's'}
              </strong>
              {' '}will be deleted
            </li>
            <li>Your Chronos link will be removed</li>
            <li>Your Trailhead sign-in itself stays — you can sign back in any time and start over</li>
          </ul>
          <div className="delete-dialog__confirm">
            <label htmlFor="kairos-delete-confirm">
              Type <code>DELETE</code> to confirm:
            </label>
            <input
              id="kairos-delete-confirm"
              type="text"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={busy}
              aria-label="Type DELETE to confirm"
            />
          </div>
        </div>
        <div className="delete-dialog__actions">
          <button
            type="button"
            className="delete-dialog__cancel"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-dialog__confirm-btn"
            onClick={onConfirm}
            disabled={!ready || busy}
          >
            {busy ? 'Deleting…' : 'Delete everything'}
          </button>
        </div>
      </div>
    </div>
  );
}
